import shelflixLogoBlack from '@/assets/shelflix-logo-black.png';
import { useHmlStatus } from '@/hooks/useHmlStatus';
import { useConfigStore } from '@/store/configStore';
import { Card } from '@shelflix/ui-web/components/ui/card';
import { Toaster } from 'sonner';
import { useEffect, useState } from 'react';

interface LoginLayoutProps {
  children: React.ReactNode;
}

// Base height for wide logos (aspect ratio >= 2:1)
const BASE_HEIGHT = 48; // 12 * 4 = 48px (h-12)
// Double height for square/tall logos (aspect ratio < 2:1)
const DOUBLE_HEIGHT = 96; // 24 * 4 = 96px (h-24)

/**
 * Calculate logo height based on aspect ratio
 * - Wide logos (ratio >= 2): use base height (48px)
 * - Square/tall logos (ratio < 2): use double height (96px)
 */
function calculateLogoHeight(width: number, height: number): number {
  const aspectRatio = width / height;
  return aspectRatio >= 2 ? BASE_HEIGHT : DOUBLE_HEIGHT;
}

export default function LoginLayout({ children }: LoginLayoutProps) {
  const applicationConfig = useConfigStore();
  const [logoHeight, setLogoHeight] = useState(BASE_HEIGHT);
  const hmlStatus = useHmlStatus();

  // Login page always uses light mode, so we always use the light logo as fallback
  const mainLogo = applicationConfig?.config?.logoUrl ?? shelflixLogoBlack;

  // Load image to detect dimensions and calculate appropriate height
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      const height = calculateLogoHeight(img.naturalWidth, img.naturalHeight);
      setLogoHeight(height);
    };
    img.onerror = () => {
      // On error, reset to base height
      setLogoHeight(BASE_HEIGHT);
    };
    img.src = mainLogo;
  }, [mainLogo]);

  return (
    <div className="flex h-screen w-screen flex-col scheme-only-light">
      <Toaster richColors position="top-right" />
      <div className="flex flex-1 items-center justify-center bg-[var(--primary)]">
        <Card className="w-full max-w-md rounded-md p-8 shadow-lg">
          <div className="mt-3 mb-6 flex flex-col items-center justify-center">
            {hmlStatus !== null && (
              <div className="mb-6 flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium">
                <span
                  className={`inline-block h-2 w-2 rounded-full ${hmlStatus === 'online' ? 'bg-green-500' : 'bg-red-500'}`}
                />
                <span className="text-gray-600">HML Status: {hmlStatus === 'online' ? 'Online' : 'Offline'}</span>
              </div>
            )}
            <img
              src={mainLogo}
              alt="Logo"
              style={{ height: `${logoHeight}px`, maxWidth: '100%', objectFit: 'contain' }}
              onError={(e) => {
                e.currentTarget.src = shelflixLogoBlack;
                setLogoHeight(BASE_HEIGHT);
              }}
            />
            <div className="mt-2 flex items-center gap-1">
              <span>Powered by</span>
              <img className="h-6.5" alt="Group" src={shelflixLogoBlack} />
            </div>
          </div>
          {children}
          <div className="mb-2" />
        </Card>
      </div>
    </div>
  );
}
